import { presetPairs, presetFromCents, shortProductTitle } from '#bundle-presets-state';
import { formatMoney, mergeChunkProducts } from '#bundle-configurator-state';
import { ensureBasePrices } from '#bundle-base-prices';

export { shortProductTitle };

function element(tag, className, text) {
  const node = document.createElement(tag);
  node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

if (!customElements.get('bundle-presets')) {
  customElements.define('bundle-presets', class extends HTMLElement {
    async connectedCallback() {
      await customElements.whenDefined('bundle-configurator');
      if (!this.isConnected) return;
      this.parent = this.closest('bundle-configurator');
      const data = this.parent?.data;
      if (!data) return;
      const started = Object.keys(this.parent.selections || {}).length > 0
        || data.steps.some((step) => new URL(location.href).searchParams.has(step.param));
      if (started) return;
      this.data = data;
      this.chips = this.querySelector('[data-preset-chips]');
      let definitions = [];
      try {
        definitions = JSON.parse(this.querySelector('[data-preset-definitions]').textContent);
      } catch (error) {
        return;
      }
      // Merge every preset's products first, then (outside the home
      // country) load their home prices, so the chips show the set price
      // the configurator will charge (2026-10-07). A failed load prices the
      // chips at the cap, as the configurator would.
      const products = definitions.map((definition) => this.mergeProducts(definition));
      await ensureBasePrices(this.data);
      if (!this.isConnected) return;
      this.presets = definitions
        .map((definition, i) => this.resolve(definition, products[i]))
        .filter((preset) => preset.pairs.length);
      this.presets.forEach((preset, index) => this.chips.append(this.renderChip(preset, index)));
      this.hidden = this.presets.length === 0;
      this.renderMore();
      this.layout();
      if (typeof ResizeObserver === 'function') {
        this.resizeObserver = new ResizeObserver(() => this.layout());
        this.resizeObserver.observe(this.chips);
      }
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || this.openIndex == null) return;
        event.stopPropagation();
        this.close({ focus: true });
      });
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
    }

    // Grid layouts show one row of sets and a button for the rest
    // (2026-10-25, Gero): with the summary column beside it, six sets took
    // three rows (~830 px) before the first ski. Phones keep the sideways
    // row with every set, which is one row high anyway.
    renderMore() {
      if (!this.dataset.showAll || this.presets.length < 2) return;
      this.chips.id ||= `Preset-chips-${this.dataset.sectionId}`;
      this.more = element('div', 'bundle-presets__more');
      this.more.hidden = true;
      this.moreButton = element('button', 'bundle-presets__more-button');
      this.moreButton.type = 'button';
      this.moreButton.setAttribute('aria-controls', this.chips.id);
      this.moreButton.addEventListener('click', () => {
        this.expanded = !this.expanded;
        this.layout({ force: true });
        if (this.expanded) this.track('presetMore', { count: this.cards.length });
        // Collapsing pulls the page up under the pointer; keep the button in view.
        if (!this.expanded) this.moreButton.scrollIntoView?.({ block: 'nearest' });
      });
      this.more.append(this.moreButton);
      this.chips.after(this.more);
    }

    // Cards per grid row; 0 while the row scrolls sideways (phones).
    columns() {
      const style = getComputedStyle(this.chips);
      if (style.display !== 'grid') return 0;
      return style.gridTemplateColumns.split(' ').filter(Boolean).length;
    }

    layout({ force = false } = {}) {
      if (!this.cards?.length) return;
      // Only a new width can change the column count; the height changes
      // this causes come straight back through the observer.
      const width = this.chips.clientWidth;
      if (!force && width === this.lastWidth) return;
      this.lastWidth = width;
      const columns = this.columns();
      const collapsible = Boolean(this.more) && columns > 0 && this.cards.length > columns;
      const limit = collapsible && !this.expanded ? columns : this.cards.length;
      if (this.openIndex != null && this.openIndex >= limit) this.close();
      this.cards.forEach((card, i) => { card.hidden = i >= limit; });
      if (!this.more) return;
      this.more.hidden = !collapsible;
      this.moreButton.setAttribute('aria-expanded', String(Boolean(this.expanded)));
      this.moreButton.textContent = this.expanded ? this.dataset.showLess : this.dataset.showAll.replace('[count]', String(this.cards.length));
    }

    // Merge the preset's products into the blob (dedupes against what the
    // page already carries), then pair.
    mergeProducts(definition) {
      return (definition.products || []).map((item) => {
        mergeChunkProducts(this.data, item.stepHandle, [item.product]);
        return { stepHandle: item.stepHandle, productHandle: item.product.handle };
      });
    }

    resolve(definition, products) {
      const preset = { handle: definition.handle, title: definition.title, claim: definition.claim, tip: definition.tip, setUrl: definition.setUrl, products };
      return { ...preset, pairs: presetPairs(this.data, preset) };
    }

    money(cents) {
      const span = document.createElement('span');
      span.innerHTML = formatMoney(cents, this.data.moneyFormat);
      return span.textContent;
    }

    partsLine(preset) {
      return preset.products
        .map((item) => shortProductTitle(this.data.steps.find((s) => s.handle === item.stepHandle)?.products.find((p) => p.handle === item.productHandle)?.title))
        .filter(Boolean)
        .join(' + ');
    }

    // The cheapest sellable length prices the card ("ab"); its compare sum
    // gives the UVP and the saving.
    cheapestPair(preset) {
      return preset.pairs.reduce((best, pair) => (best == null || pair.cents < best.cents ? pair : best), null);
    }

    // One card per set (2026-10-25): the card face (image, name, price,
    // parts, claim) with a single "Länge wählen" button whose ::before covers
    // the whole card, so a tap anywhere opens the length choice. The choice
    // opens INSIDE the clicked card, in the same grid cell as the face: with
    // two rows of sets a shared panel below the grid opened below the fold
    // and the customer never saw it. The set-page link lives in the open
    // choice only, once per set instead of under every card.
    renderChip(preset, index) {
      const cheapest = this.cheapestPair(preset);
      const price = this.priceText(preset);
      const parts = this.partsLine(preset);
      const id = `Preset-${this.dataset.sectionId}-${index}`;
      const card = element('article', 'bundle-presets__card');
      card.setAttribute('aria-labelledby', `${id}-title`);
      const face = element('div', 'bundle-presets__face');
      const media = element('span', 'bundle-presets__media');
      media.setAttribute('aria-hidden', 'true');
      const imageSrc = cheapest?.ski?.image;
      if (imageSrc) {
        const img = document.createElement('img');
        img.src = imageSrc;
        img.alt = shortProductTitle(cheapest.ski.title) || preset.title;
        img.width = 88;
        img.height = 88;
        img.loading = 'lazy';
        media.append(img);
      }
      const summary = element('div', 'bundle-presets__summary');
      const title = element('h4', 'bundle-presets__chip-title', preset.title);
      title.id = `${id}-title`;
      summary.append(title, this.priceRow(cheapest, price));
      // The tip rides on the card's top edge (CSS), so a card with and one
      // without it line up row by row.
      if (preset.tip) face.append(element('span', 'bundle-presets__chip-tip product-badge product-badge--expert', this.dataset.tip));
      const body = element('div', 'bundle-presets__body');
      body.append(element('p', 'bundle-presets__chip-parts', parts));
      if (preset.claim) body.append(element('p', 'bundle-presets__chip-claim', preset.claim));
      const foot = element('div', 'bundle-presets__foot');
      const chip = element('button', 'bundle-presets__chip', this.dataset.pickLength);
      chip.type = 'button';
      chip.id = id;
      chip.setAttribute('aria-expanded', 'false');
      chip.setAttribute('aria-controls', `${id}-lengths`);
      chip.setAttribute('aria-label', this.dataset.chipLabel.replace('[set]', preset.title).replace('[parts]', parts).replace('[price]', price));
      chip.addEventListener('click', () => this.toggle(index));
      foot.append(chip);
      face.append(media, summary, body, foot);
      card.append(face, this.renderPicker(preset, index, id, price));
      (this.cards ||= [])[index] = card;
      return card;
    }

    // "ab 675 €" only when the lengths differ in price; one price for every
    // length (the usual case) reads as exactly that.
    priceText(preset) {
      const from = this.money(presetFromCents(preset.pairs));
      return new Set(preset.pairs.map((pair) => pair.cents)).size > 1 ? this.dataset.from.replace('[price]', from) : from;
    }

    // Price with UVP and saving: the cheapest sellable length prices the
    // card; its compare sum gives the UVP and the saving. UVP and saving wrap
    // as one, so the badge never sits alone on a line.
    priceRow(cheapest, price) {
      const row = element('p', 'bundle-presets__price-row');
      row.append(element('span', 'bundle-presets__chip-price', price));
      const compare = cheapest?.compareCents || 0;
      if (compare > (cheapest?.cents || 0)) {
        const reference = element('span', 'bundle-presets__chip-compare');
        reference.append(element('span', 'bundle-presets__chip-uvp', (this.dataset.uvp || '[price]').replace('[price]', this.money(compare))));
        const pct = Math.round(((compare - cheapest.cents) / compare) * 100);
        if (pct > 0 && this.dataset.saving) {
          const saving = element('span', 'bundle-presets__chip-saving product-badge product-badge--sale', this.dataset.saving.replace('[pct]', String(pct)));
          if (this.dataset.savingLabel) saving.setAttribute('aria-label', this.dataset.savingLabel.replace('[pct]', String(pct)));
          reference.append(saving);
        }
        row.append(reference);
      }
      return row;
    }

    // The length choice, built with the card and kept inert until opened.
    // It shares the face's grid cell, so the card keeps its size when it
    // opens (the taller of the two sets the row). One price for every length
    // (the usual case) shows once in the head; otherwise each pill names its
    // own.
    renderPicker(preset, index, id, price) {
      const picker = element('div', 'bundle-presets__picker');
      picker.id = `${id}-lengths`;
      picker.setAttribute('role', 'group');
      picker.setAttribute('aria-labelledby', `${id}-prompt`);
      picker.toggleAttribute('inert', true);
      const prices = new Set(preset.pairs.map((pair) => pair.cents));
      const head = element('div', 'bundle-presets__picker-head');
      const name = element('p', 'bundle-presets__picker-title');
      name.append(element('span', 'bundle-presets__picker-name', preset.title));
      name.append(element('span', 'bundle-presets__chip-price', price));
      const close = element('button', 'bundle-presets__close');
      close.type = 'button';
      close.setAttribute('aria-label', this.dataset.close);
      close.addEventListener('click', () => this.close({ focus: true }));
      head.append(name, close);
      const prompt = element('p', 'bundle-presets__prompt text-body-14-semibold', this.dataset.lengthPrompt.replace('[ski]', shortProductTitle(preset.pairs[0].ski.title)));
      prompt.id = `${id}-prompt`;
      const pills = element('div', `bundle-presets__pills${prices.size > 1 ? ' bundle-presets__pills--priced' : ''}`);
      pills.setAttribute('role', 'group');
      pills.setAttribute('aria-label', this.dataset.lengthLabel);
      preset.pairs.forEach((pair) => {
        const pill = element('button', 'bundle-variant-panel__pill text-body-16-semibold', pair.skiVariant.title);
        pill.type = 'button';
        pill.setAttribute('aria-label', `${pair.skiVariant.title}, ${this.money(pair.cents)}`);
        if (prices.size > 1) pill.append(element('span', 'bundle-presets__pill-price text-body-12', this.money(pair.cents)));
        // The choice opens under the pointer: the second click of a double
        // click on the card would land on a length. Only a single click (or
        // Enter/Space, detail 0) picks.
        pill.addEventListener('click', (event) => { if (event.detail <= 1) this.apply(preset, pair, pill); });
        pills.append(pill);
      });
      const foot = element('div', 'bundle-presets__picker-foot');
      foot.append(element('p', 'bundle-presets__hint text-body-12', this.dataset.hint));
      if (preset.setUrl) {
        const link = element('a', 'bundle-presets__set-link', this.dataset.setLink);
        link.href = preset.setUrl;
        link.addEventListener('click', () => this.track('presetExit', { preset: preset.handle }));
        foot.append(link);
      }
      picker.append(head, prompt, pills, foot);
      return picker;
    }

    toggle(index) {
      if (this.openIndex === index) return this.close({ focus: true });
      this.close();
      const card = this.cards[index];
      if (!card) return;
      this.openIndex = index;
      card.classList.add('is-open');
      card.querySelector('.bundle-presets__chip').setAttribute('aria-expanded', 'true');
      card.querySelector('.bundle-presets__picker').toggleAttribute('inert', false);
      // Focus first, then make the face inert: the clicked button sits on it.
      card.querySelector('.bundle-variant-panel__pill')?.focus({ preventScroll: true });
      card.querySelector('.bundle-presets__face').toggleAttribute('inert', true);
      this.track('presetOpen', { preset: this.presets[index]?.handle, position: index + 1 });
      // The card is where the customer clicked; this only matters when it
      // was partly under the sticky header or (phones) half off the row.
      card.scrollIntoView?.({ block: 'nearest', inline: 'nearest', behavior: globalThis.Theme?.config?.motionReduced ? 'auto' : 'smooth' });
    }

    close({ focus = false } = {}) {
      const card = this.cards?.[this.openIndex];
      this.openIndex = null;
      if (!card) return;
      card.classList.remove('is-open');
      card.querySelector('.bundle-presets__face').toggleAttribute('inert', false);
      card.querySelector('.bundle-presets__picker').toggleAttribute('inert', true);
      const chip = card.querySelector('.bundle-presets__chip');
      chip.setAttribute('aria-expanded', 'false');
      if (focus) chip.focus({ preventScroll: true });
    }

    // Through the configurator's guarded door (its track() swallows every
    // failure); a page without the running app has nothing to send to.
    track(method, params) {
      try {
        this.parent?.track?.(method, params);
      } catch {
        // analytics must never break the flow
      }
    }

    hasCard(stepHandle, productHandle) {
      const esc = (value) => (globalThis.CSS?.escape ? CSS.escape(value) : String(value).replace(/["\\]/g, '\\$&'));
      return Boolean(this.parent.querySelector(`[data-step-panel="${esc(stepHandle)}"] .bundle-product-card[data-product-handle="${esc(productHandle)}"]`));
    }

    // Every part in step order through the app (the same duck-typed call the
    // variant panel makes), via 'preset' for the funnel. A merged product
    // without a card yet gets its card fetched so the grid shows the pick.
    async apply(preset, pair, pill) {
      if (typeof this.parent.onSelect !== 'function') return this.navigate(pair);
      // The chosen length stays marked while the parts go in, so the tap
      // visibly landed; nothing in the card takes a second tap meanwhile.
      pill?.setAttribute('aria-pressed', 'true');
      const picker = pill?.closest('.bundle-presets__picker');
      picker?.setAttribute('aria-busy', 'true');
      picker?.querySelectorAll('button').forEach((button) => { button.disabled = true; });
      // Before the parts go in, so their selects and the later complete and
      // atc_result name the set (cfg_preset).
      this.track('pickPreset', preset.handle);
      for (const item of preset.products) {
        const variantId = pair.selections[item.stepHandle];
        if (variantId == null) continue;
        await this.parent.onSelect({ dataset: { step: item.stepHandle, variantId: String(variantId) } }, 'preset');
        if (!this.hasCard(item.stepHandle, item.productHandle) && typeof this.parent.fetchMissingSelectedCard === 'function') {
          await this.parent.fetchMissingSelectedCard(item.stepHandle, [item.productHandle]);
        }
      }
      // The set has started: the row has done its job.
      this.hidden = true;
    }

    navigate(pair) {
      const url = new URL(location.href);
      this.data.steps.forEach((step) => url.searchParams.delete(step.param));
      ['mount', 'bsl', 'fit', 'edit'].forEach((key) => url.searchParams.delete(key));
      new URLSearchParams(pair.params).forEach((value, key) => url.searchParams.set(key, value));
      url.hash = `shopify-section-${this.dataset.sectionId}`;
      this.go(url);
    }

    go(url) {
      location.assign(url);
    }
  });
}
